// src/pages/login.jsx
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import "./login.css";

// ⚡ use the thin auth service wrappers
import { login as authLogin, signupAndLogin } from '../services/auth';

export default function Login({ open = true, onClose, onSuccess }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [email, setEmail] = useState('');
  const [name, setName] = useState(''); // signup only
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // unified close handler (works for modal or page)
  const handleClose = useCallback(() => {
    if (loading) return;
    if (onClose) onClose();
    else {
      // used as a route page: go back if possible, else go home
      if (window.history.length > 1) navigate(-1);
      else navigate("/");
    }
  }, [loading, onClose, navigate]);

  // reset form each time it opens
  useEffect(() => {
    if (open) {
      setMode('login');
      setEmail('');
      setName('');
      setPassword('');
      setShowPwd(false);
      setLoading(false);
      setErr('');
    }
  }, [open]);

  // close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, handleClose]);

  if (!open) return null;

  async function doLogin(e) {
    e?.preventDefault();
    if (!email || !password) return; // guard
    setErr('');
    setLoading(true);
    try {
      const { user } = await authLogin({ email, password });
      onSuccess?.(user || null);
      handleClose();
    } catch (e2) {
      setErr(e2?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  async function doSignup(e) {
    e?.preventDefault();
    if (!name || !email || !password) return; // guard
    setErr('');
    setLoading(true);
    try {
      const { user } = await signupAndLogin({ name, email, password });
      onSuccess?.(user || null);
      handleClose();
    } catch (e2) {
      setErr(e2?.message || 'Sign-up failed');
    } finally {
      setLoading(false);
    }
  }

  const submitDisabled =
    loading || !email || !password || (mode === 'signup' && !name.trim());

  return (
    <div className="auth-backdrop" onClick={handleClose}>
      <div
        className="auth-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="auth-head">
          <div className="auth-toggle">
            <button
              type="button"
              className={`auth-chip ${mode === 'login' ? 'is-active' : ''}`}
              onClick={() => !loading && (setMode('login'), setErr(''))}
              aria-pressed={mode === 'login'}
            >
              Sign in
            </button>
            <button
              type="button"
              className={`auth-chip ${mode === 'signup' ? 'is-active' : ''}`}
              onClick={() => !loading && (setMode('signup'), setErr(''))}
              aria-pressed={mode === 'signup'}
            >
              Sign up
            </button>
          </div>
          <button className="auth-x" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <form
          className="auth-body"
          onSubmit={mode === 'login' ? doLogin : doSignup}
          noValidate
        >
          <h3 id="auth-title" className="auth-title">
            {mode === 'login' ? 'Welcome back' : 'Create your account'}
          </h3>

          {err ? (
            <p className="auth-error" role="alert">
              {err}
            </p>
          ) : null}

          {mode === 'signup' && (
            <label className="auth-label">
              Name
              <input
                className="auth-input"
                type="text"
                placeholder='Your full name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
              />
            </label>
          )}

          <label className="auth-label">
            Email
            <input
              className="auth-input"
              type="email"
              placeholder='you@example.com'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus={mode === 'login'}
            />
          </label>

          <label className="auth-label">
            Password
            <div className="auth-input-wrap">
              <input
                className="auth-input"
                type={showPwd ? 'text' : 'password'}
                placeholder='••••••••'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
              <button
                type="button"
                className="auth-link"
                onClick={() => setShowPwd((s) => !s)}
              >
                {showPwd ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>

          <button className="auth-btn auth-btn--block" disabled={submitDisabled}>
            {loading
              ? mode === 'login'
                ? 'Signing in…'
                : 'Creating…'
              : mode === 'login'
              ? 'Sign in'
              : 'Create account'}
          </button>
        </form>
      </div>
    </div>
  );
}
